// ═════════════════════════════════════════════════════════════════════════════
// SNOMED CT concept search.
//
// This talks to the MAIN Django backend, not the NHCX coverage service — same
// endpoints and request body the doctor console uses (see
// doctor-console/Sections/Procedures.js and ChiefComplaint.js).
//
// snomed/search/ returns matching concepts. When it comes back empty the console
// falls back to snomed/suggest/, whose rows are looser *spelling* suggestions to
// search again with rather than concepts to pick — they carry no usable conceptId.
// That distinction matters here: coverage eligibility needs a real code, so the
// caller is told which of the two it got and offers them differently.
// ═════════════════════════════════════════════════════════════════════════════

import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_ROUTE_URL;

// The doctor console caps the list at 300 — beyond that the dropdown is unusable
// and the payload is large. Same cap here.
const MAX_OPTIONS = 300;

const searchBody = (term, semanticTag) => ({
  term,
  state: "active",
  semantictag: semanticTag,
  acceptability: "synonyms",
  returnlimit: "100",
  refsetid: null,
  parentid: null,
  excludeparentconcept: false,
  fullconcept: true,
});

// Bearer is attached per call site in this codebase; APP-MODE / ORG-CODE are added
// by the global interceptor because these URLs are not on its exclusion list.
const authHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("accessToken") || ""}`,
});

const rowsOf = (res) => (res?.data?.status === "Success" ? res.data.data || [] : []);

/**
 * Search SNOMED concepts by name.
 *
 * Resolves `{ concepts, fromSuggest }`:
 *   fromSuggest false → concepts are real, each with a conceptId, safe to select.
 *   fromSuggest true  → no exact match; these are alternative search terms only,
 *                       so the UI should re-search with one rather than select it.
 */
export async function searchSnomedConcepts(term, semanticTag) {
  const headers = authHeaders();
  const body = searchBody(term, semanticTag);

  const found = rowsOf(await axios.post(`${BASE}snomed/search/`, body, { headers }));
  if (found.length) {
    return {
      concepts: found
        .slice(0, MAX_OPTIONS)
        .map((r) => ({ term: r.term, code: r.conceptId }))
        .filter((c) => c.term && c.code),
      fromSuggest: false,
    };
  }

  const suggested = rowsOf(await axios.post(`${BASE}snomed/suggest/`, body, { headers }));
  return {
    concepts: suggested
      .slice(0, MAX_OPTIONS)
      .map((r) => ({ term: r.term, code: "" }))
      .filter((c) => c.term),
    fromSuggest: suggested.length > 0,
  };
}
